import { useState, useMemo } from 'react';
import { Row, Col, Card, Button, Form, Badge, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../providers/AuthProvider.js';
import {
  useOperators,
  useCreateOperator,
  useUpdateOperator,
  useDeleteOperator
} from '../../features/operators/hooks/useOperators.js';
import { SHIFTS, SHIFT_COLORS } from '../../data/operatorConstants.js';
import OperatorCard from '../../components/operators/OperatorCard.js';
import OperatorFormModal from '../../components/operators/OperatorFormModal.js';
import DeleteOperatorModal from '../../components/operators/DeleteOperatorModal.js';

const OperatorList = () => {
  const [authState] = useAuthContext();
  const isAdmin = authState.user?.role === 'admin';
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [formModal, setFormModal] = useState({ show: false, operator: null });
  const [deleteModal, setDeleteModal] = useState({ show: false, operator: null });

  const { data, isLoading } = useOperators();
  const createMutation = useCreateOperator();
  const updateMutation = useUpdateOperator();
  const deleteMutation = useDeleteOperator();

  const operators = data?.data || [];

  const filtered = useMemo(() => {
    return operators.filter(op => {
      if (!showInactive && !op.active) return false;
      if (search && !op.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [operators, search, showInactive]);

  const byShift = useMemo(() => {
    const groups = {};
    SHIFTS.forEach(s => (groups[s] = []));
    filtered.forEach(op => {
      if (groups[op.shift]) groups[op.shift].push(op);
    });
    return groups;
  }, [filtered]);

  const handleFormSubmit = async payload => {
    if (formModal.operator) {
      await updateMutation.mutateAsync(payload);
    } else {
      await createMutation.mutateAsync(payload);
    }
    setFormModal({ show: false, operator: null });
  };

  const handleDelete = async id => {
    await deleteMutation.mutateAsync(id);
    setDeleteModal({ show: false, operator: null });
  };

  const isMutating = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="mb-9 mt-0">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">Operators</h2>
          <p className="text-700 mb-0">
            Manage operators by shift, skills, and preferred machines
          </p>
        </div>
        {isAdmin && (
          <Button
            variant="primary"
            onClick={() => setFormModal({ show: true, operator: null })}
          >
            <FontAwesomeIcon icon="plus" className="me-2" />
            Add Operator
          </Button>
        )}
      </div>

      {/* Filter bar */}
      <Card className="mb-4">
        <Card.Body className="py-3">
          <Row className="g-3 align-items-center">
            <Col xs={12} sm={6} md={4} lg={3}>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Search operator..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </Col>
            <Col xs="auto">
              <Form.Check
                type="switch"
                id="show-inactive"
                label="Show inactive"
                checked={showInactive}
                onChange={e => setShowInactive(e.target.checked)}
              />
            </Col>
            <Col xs="auto" className="ms-auto">
              <span className="text-700 fs-9">
                {filtered.length} operators
              </span>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {isLoading ? (
        <div className="d-flex align-items-center justify-content-center py-6">
          <Spinner animation="border" variant="primary" size="sm" className="me-2" />
          <span className="text-700">Loading...</span>
        </div>
      ) : (
        <Row className="g-3">
          {SHIFTS.map(shift => (
            <Col key={shift} xs={12} md={4}>
              <Card className="h-100">
                <Card.Header
                  className="py-2 d-flex justify-content-between align-items-center"
                  style={{ borderBottom: `3px solid var(--falcon-${SHIFT_COLORS[shift]})` }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <Badge bg={SHIFT_COLORS[shift]}>Shift {shift}</Badge>
                    <span className="text-700 fs-9">
                      {byShift[shift].length} operators
                    </span>
                  </div>
                </Card.Header>
                <Card.Body className="p-2">
                  {byShift[shift].length === 0 ? (
                    <p className="text-center text-700 fs-9 py-3 mb-0">
                      No operators on this shift
                    </p>
                  ) : (
                    byShift[shift].map(op => (
                      <div
                        key={op._id}
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/apps/operators/${op._id}`)}
                      >
                        <OperatorCard
                          operator={op}
                          isAdmin={isAdmin}
                          onEdit={operator =>
                            setFormModal({ show: true, operator })
                          }
                          onDelete={operator =>
                            setDeleteModal({ show: true, operator })
                          }
                        />
                      </div>
                    ))
                  )}
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <OperatorFormModal
        show={formModal.show}
        onHide={() => setFormModal({ show: false, operator: null })}
        operator={formModal.operator}
        onSubmit={handleFormSubmit}
        isLoading={isMutating}
      />

      <DeleteOperatorModal
        show={deleteModal.show}
        onHide={() => setDeleteModal({ show: false, operator: null })}
        operator={deleteModal.operator}
        onConfirm={handleDelete}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
};

export default OperatorList;
